import { useCallback, useState } from "react";

import styled from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import Sentry from "@similarweb/sentry-logger";
import { colorsPalettes } from "@similarweb/styles";
import { IconButton, PromoBanner } from "@similarweb/ui-components";

import type { CrmIntegrationProvider } from "app/pages/sales-intelligence/domain/crm-integration/types/provider";
import { SalesIntelligenceExcelClient } from "app/pages/sales-intelligence/services/excelClient";
import integrationTrackingService from "app/pages/sales-intelligence/services/tracking/integrationTrackingService";
import type { ExportName } from "app/pages/sales-intelligence/sub-modules/management/store/type";

import { getAllowedValuesExcelUrl } from "../allowedValuesExcel";
import {
    ALLOWED_VALUES_EXCEL_DESCRIPTION,
    ALLOWED_VALUES_EXCEL_DOWNLOAD,
    ALLOWED_VALUES_EXCEL_PROMPT,
} from "../translations";

const StyledAllowedValuesExcelBanner = styled(PromoBanner)`
    padding: 20px 24px;
    background-color: ${colorsPalettes.blue[100]};
`;

type AllowedValuesExcelDownloadProps = {
    crmProvider: CrmIntegrationProvider;
    exportName: ExportName;
};

export const AllowedValuesExcelDownload = ({ crmProvider, exportName }: AllowedValuesExcelDownloadProps) => {
    const { translate } = useTranslate();
    const [isDownloading, setIsDownloading] = useState(false);

    const handleDownload = useCallback(async (): Promise<void> => {
        if (isDownloading) {
            return;
        }

        const url = getAllowedValuesExcelUrl(crmProvider);

        setIsDownloading(true);
        integrationTrackingService.trackDownloadAllowedValuesExcel(crmProvider, exportName);

        try {
            const result = await SalesIntelligenceExcelClient(url);

            if (!result.success) {
                Sentry.errorTracker({
                    e: result.error,
                    message: "Failed to download allowed values Excel",
                    level: "error",
                    params: {
                        extra: { crmProvider, exportName, url },
                    },
                });
            }
        } catch (error) {
            Sentry.errorTracker({
                e: error,
                message: "Failed to download allowed values Excel",
                level: "error",
                params: {
                    extra: { crmProvider, exportName, url },
                },
            });
        } finally {
            setIsDownloading(false);
        }
    }, [crmProvider, exportName, isDownloading]);

    return (
        <StyledAllowedValuesExcelBanner data-automation="export-table-allowed-values-excel">
            <PromoBanner.Content>
                <PromoBanner.Title>{translate(ALLOWED_VALUES_EXCEL_PROMPT)}</PromoBanner.Title>
                <PromoBanner.Description>{translate(ALLOWED_VALUES_EXCEL_DESCRIPTION)}</PromoBanner.Description>
            </PromoBanner.Content>
            <IconButton
                type="outlined"
                iconName="download"
                isLoading={isDownloading}
                showLoaderIcon={false}
                isDisabled={isDownloading}
                onClick={() => {
                    void handleDownload();
                }}
                dataAutomation="export-table-allowed-values-excel-download"
            >
                {translate(ALLOWED_VALUES_EXCEL_DOWNLOAD)}
            </IconButton>
        </StyledAllowedValuesExcelBanner>
    );
};
